import {useEffect, useState} from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useForm } from "react-hook-form";
import { generalInfoState } from "../../../../../search-repair-shop/state";
import { FormTextField } from "../../../../../common/form-fields-wrappers";
import { setValuesInForm } from "../../../../constants";
import { IGeneralInfo } from "./types";

function typedObjectKeys<T extends object>(obj: T) {
    return Object.keys(obj) as Array<keyof T>;
}

const GeneralInfo = () => {
    const {t} = useTranslation(["network", "translation"]);
    const {t: serviceTypeTranslations} = useTranslation(["servicetype", "translation"]);
    const generalInfo = useRecoilValue(generalInfoState);

    const [serviceTypeItCodes, setServiceTypeItCodes] = useState<Record<string,string>>({});
    useEffect(() => {
        (async () => {
            const codes = await fetch('/locales/it/servicetype.json').then(res => res.json());
            setServiceTypeItCodes(codes);
        })();
    }, []);

    const generalInfoForm = useForm<IGeneralInfo | { serviceTypeTranslated: string }>({ defaultValues: {
        id: generalInfo?.id ?? -1,
        repairShopNaming: generalInfo?.repairShopNaming ?? '',
        bpCode: generalInfo?.bpCode ?? '',
        identificationCode: generalInfo?.identificationCode ?? '',
        oldIdentificationCode: generalInfo?.oldIdentificationCode ?? '',
        currentStatus: generalInfo?.currentStatus ?? '',
        currentAttribute: generalInfo?.currentAttribute ?? '',
        category: generalInfo?.category ?? '',
        serviceType: generalInfo?.serviceType ?? [],
        serviceTypeTranslated: generalInfo?.serviceType ?? '',
        brandName: generalInfo?.brandName ?? '',
        city: generalInfo?.city ?? '',
        address: generalInfo?.address ?? '',
        streetNumber: generalInfo?.streetNumber ?? '',
        country: generalInfo?.country ?? '',
        region: generalInfo.region?.description ?? '',
        province: generalInfo?.province ?? '',
        postalCode: generalInfo?.postalCode ?? '',
        longitude: generalInfo?.longitude ?? '',
        latitude: generalInfo?.latitude ?? '',
        phoneNumber: generalInfo?.phoneNumber ?? '',
        cellPhoneNumber: generalInfo?.cellPhoneNumber ?? '',
        fax: generalInfo?.fax ?? '',
        email: generalInfo?.email ?? '',
        branchManager: generalInfo?.branchManager ?? '',
        branchManagerCellPhoneNumber: generalInfo?.branchManagerCellPhoneNumber ?? '',
        area: generalInfo?.area?.description ?? '',
        areaManager: generalInfo?.areaManager ?? '',
    } });

    useEffect(() => {
        if (!!generalInfo) {
            setValuesInForm(generalInfo, generalInfoForm, {region: (x) => x?.description || '', area: (x) => x?.description || ''});
        }
    },[generalInfo])

    const serviceType = generalInfoForm.watch("serviceType");
    useEffect(() => {
        const codes = (serviceType as string[]).map((st) => typedObjectKeys(serviceTypeItCodes).find((c) => serviceTypeItCodes[c] === st));
        const translated = codes.map(serviceTypeTranslations);
        generalInfoForm.setValue("serviceTypeTranslated", translated.join(","));
    }, [serviceType, serviceTypeItCodes, t]);

    return(
        <Grid container direction="row" alignItems="start">
            <Grid container item direction="row" xs={12} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="repairShopNaming"
                        label={t("rsDetail.generalInfo.form.label.repairShopNaming")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="bpCode"
                        label={t("rsDetail.generalInfo.form.label.sapId")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="identificationCode"
                        label={t("rsDetail.generalInfo.form.label.identificationCode")}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="oldIdentificationCode"
                        label={t("rsDetail.generalInfo.form.label.oldIdentificationCode")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="currentStatus"
                        label={t("rsDetail.generalInfo.form.label.currentStatus")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="currentAttribute"
                        label={t("rsDetail.generalInfo.form.label.currentAttribute")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="category"
                        label={t("rsDetail.generalInfo.form.label.category")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="serviceTypeTranslated"
                        label={t("rsDetail.generalInfo.form.label.serviceType")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="brandName"
                        label={t("rsDetail.generalInfo.form.label.brandName")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="city"
                        label={t("rsDetail.generalInfo.form.label.city")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="address"
                        label={t("rsDetail.generalInfo.form.label.address")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="streetNumber"
                        label={t("rsDetail.generalInfo.form.label.streetNumber")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="country"
                        label={t("rsDetail.generalInfo.form.label.country")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="region"
                        label={t("rsDetail.generalInfo.form.label.region")}
                    />
                </Grid>

                <Grid item xs={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="province"
                        label={t("rsDetail.generalInfo.form.label.province")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="postalCode"
                        label={t("rsDetail.generalInfo.form.label.postalCode")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="longitude"
                        label={t("rsDetail.generalInfo.form.label.longitude")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="latitude"
                        label={t("rsDetail.generalInfo.form.label.latitude")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="phoneNumber"
                        label={t("rsDetail.generalInfo.form.label.phoneNumber")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="cellPhoneNumber"
                        label={t("rsDetail.generalInfo.form.label.cellPhoneNumber")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="fax"
                        label={t("rsDetail.generalInfo.form.label.fax")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="email"
                        label={t("rsDetail.generalInfo.form.label.email")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="branchManager"
                        label={t("rsDetail.generalInfo.form.label.branchManager")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="branchManagerCellPhoneNumber"
                        label={t("rsDetail.generalInfo.form.label.branchManagerCellPhoneNumber")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="area"
                        label={t("rsDetail.generalInfo.form.label.area")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={generalInfoForm.control}
                        name="areaManager"
                        label={t("rsDetail.generalInfo.form.label.areaManager")}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default GeneralInfo;
